import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Typography, Box, Card, CardContent, LinearProgress, Divider } from '@mui/material';
import {
  PeopleOutlined, MessageOutlined, CheckCircleOutlined,
  WarningAmberOutlined, TrackChangesOutlined, SmsOutlined,
  LocalHospitalOutlined, PhoneAndroidOutlined
} from '@mui/icons-material';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend
} from 'recharts';
import StatCard from '../components/StatCard';
import ReportExport from '../components/ReportExport';
import FilterBar, { EMPTY_FILTERS } from '../components/FilterBar';
import { getOverview, getEnrolmentByRegion, getDiseases } from '../utils/api';

const COLORS  = ['#00BFA5','#FFB300','#40C4FF','#FF5252','#69F0AE','#CE93D8'];
const TT_STYLE = { backgroundColor:'#111827', border:'1px solid rgba(0,191,165,0.2)', borderRadius:8 };

function EngagementBar({ label, value, total, color }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <Box mb={2}>
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" color={color} fontFamily='"IBM Plex Mono", monospace' fontWeight={600}>
          {(value||0).toLocaleString()} ({pct.toFixed(1)}%)
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={Math.min(pct,100)}
        sx={{ height:6, borderRadius:3, bgcolor:'rgba(255,255,255,0.06)',
          '& .MuiLinearProgress-bar':{ bgcolor:color, borderRadius:3 } }} />
    </Box>
  );
}

export default function OverviewPage() {
  const [filters,    setFilters]    = useState(EMPTY_FILTERS);
  const [overview,   setOverview]   = useState(null);
  const [regionData, setRegionData] = useState([]);
  const [diseases,   setDiseases]   = useState([]);
  const [loading,    setLoading]    = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, rd, ds] = await Promise.all([
        getOverview(filters),
        getEnrolmentByRegion(filters),
        getDiseases(filters),
      ]);
      setOverview(ov.data);
      setRegionData(rd.data);
      setDiseases(ds.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const telecomPie = overview ? [
    { name: 'MTN',             value: overview.mtnCount   || 0 },
    { name: 'Eswatini Mobile', value: overview.emCount    || 0 },
    { name: 'Other',           value: overview.otherCount || 0 },
  ].filter(d => d.value > 0) : [];

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700} color="primary.main">TextWell4Health Overview</Typography>
        <Typography variant="body2" color="text.secondary">TextWell4Health SMS Platform · Kingdom of Eswatini</Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
        <FilterBar filters={filters} onChange={setFilters} />
        <ReportExport />
      </Box>

      {/* KPI Row */}
      <Grid container spacing={2} mb={2}>
        {[
          { title:'Total Enrolled',  value: overview?.totalEnrolled,    icon:<PeopleOutlined />,      color:'#00BFA5' },
          { title:'Active Patients', value: overview?.activePatients,   icon:<CheckCircleOutlined />, color:'#69F0AE' },
          { title:'Messages Sent',   value: overview?.totalSent,        icon:<MessageOutlined />,     color:'#40C4FF' },
          { title:'Educational',     value: overview?.totalEducational, icon:<SmsOutlined />,         color:'#CE93D8' },
          { title:'Reminders Sent',  value: overview?.totalReminders,   icon:<LocalHospitalOutlined />,color:'#FFB300' },
          { title:'Non-Responding',  value: overview?.notResponding,    icon:<WarningAmberOutlined />, color:'#FF5252' },
        ].map(item => (
          <Grid item xs={6} sm={4} md={2} key={item.title}>
            <StatCard {...item} value={item.value?.toLocaleString() || '—'} loading={loading} small />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} mb={2}>
        {/* Region bar */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Enrolment by Region</Typography>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={regionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="region" tick={{ fontSize:11, fill:'#90A4AE', fontFamily:'IBM Plex Mono' }} />
                  <YAxis tick={{ fontSize:11, fill:'#90A4AE' }} />
                  <Tooltip contentStyle={TT_STYLE} />
                  <Legend />
                  <Bar dataKey="mtn" name="MTN"             fill="#FFB300" radius={[4,4,0,0]} />
                  <Bar dataKey="em"  name="Eswatini Mobile" fill="#00BFA5" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Telecom pie */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height:'100%' }}>
            <CardContent>
              <Typography variant="h6" mb={2}>Telecom Distribution</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={telecomPie} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                    paddingAngle={4} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}
                    labelLine={false}>
                    {telecomPie.map((_,i) => <Cell key={i} fill={['#FFB300','#00BFA5','#90A4AE'][i]} />)}
                  </Pie>
                  <Tooltip contentStyle={TT_STYLE} />
                </PieChart>
              </ResponsiveContainer>
              <Box display="flex" justifyContent="center" gap={3} mt={1}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary">MTN</Typography>
                  <Typography variant="h6" fontFamily='"IBM Plex Mono", monospace' color="#FFB300">{overview?.mtnCount?.toLocaleString() || '—'}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary">Eswatini Mobile</Typography>
                  <Typography variant="h6" fontFamily='"IBM Plex Mono", monospace' color="#00BFA5">{overview?.emCount?.toLocaleString() || '—'}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Disease breakdown */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Disease Groups</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={diseases} layout="vertical" margin={{ left:20, right:20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize:11, fill:'#90A4AE' }} />
                  <YAxis dataKey="disease" type="category" width={145} tick={{ fontSize:11, fill:'#90A4AE', fontFamily:'IBM Plex Mono' }} />
                  <Tooltip contentStyle={TT_STYLE} />
                  <Bar dataKey="total" name="Total" radius={[0,4,4,0]}>
                    {diseases.map((_,i) => <Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Engagement summary */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2.5}>
                <TrackChangesOutlined sx={{ mr:1, verticalAlign:'middle', color:'primary.main' }} />
                Engagement Summary
              </Typography>
              <EngagementBar label="In NCD Appointments"      value={overview?.attendedHospital}      total={overview?.totalEnrolled} color="#00BFA5" />
              <EngagementBar label="Responded to flow"        value={overview?.respondedToAppointment} total={overview?.totalEnrolled} color="#40C4FF" />
              <EngagementBar label="Completed flow"           value={overview?.completedFlow}          total={overview?.totalEnrolled} color="#CE93D8" />
              <EngagementBar label="Missed visit / no contact" value={overview?.notResponding}         total={overview?.totalEnrolled} color="#FF5252" />
              <Divider sx={{ my:2, borderColor:'rgba(255,255,255,0.08)' }} />
              <Grid container spacing={1}>
                {[
                  { label:'Appointment Rate', value: overview?.appointmentResponseRate+'%', color:'#00BFA5' },
                  { label:'Hospital Rate',    value: overview?.hospitalAttendanceRate+'%',  color:'#69F0AE' },
                  { label:'Flow Completion',  value: overview?.flowCompletionRate+'%',      color:'#40C4FF' },
                ].map(item => (
                  <Grid item xs={4} key={item.label}>
                    <Box textAlign="center" p={1} sx={{ bgcolor:'rgba(255,255,255,0.03)', borderRadius:2 }}>
                      <Typography variant="h6" fontFamily='"IBM Plex Mono", monospace' color={item.color} fontSize="1.1rem">
                        {item.value || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize="0.62rem" lineHeight={1.3} display="block">
                        {item.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
